import React, { createContext, useContext, useState, useEffect } from "react";
import {
	collection,
	addDoc,
	deleteDoc,
	doc,
	updateDoc,
	onSnapshot,
	query,
	orderBy,
	where,
	serverTimestamp,
	arrayUnion,
	arrayRemove,
	getDoc,
	getDocs,
	setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";
import { detectPB } from "../utils/pbDetection";
import { createPBMessage } from "../utils/messageService";

const WorkoutContext = createContext();

export function useWorkouts() {
	return useContext(WorkoutContext);
}

export function WorkoutProvider({ children }) {
	const { user } = useAuth();
	const [sharedWorkouts, setSharedWorkouts] = useState([]);
	const [userWorkouts, setUserWorkouts] = useState([]);
	const [userRecords, setUserRecords] = useState([]);
	const [allUserRecords, setAllUserRecords] = useState([]); // For trainers
	const [userEnrollments, setUserEnrollments] = useState([]);
	const [movements, setMovements] = useState([]);
	const [countTypes, setCountTypes] = useState([]);
	const [workoutScores, setWorkoutScores] = useState([]);
	const [allWorkoutScores, setAllWorkoutScores] = useState([]); // For trainers
	const [notifications, setNotifications] = useState([]);
	const [plannerEntries, setPlannerEntries] = useState([]);
	const [workshops, setWorkshops] = useState([]);
	const [userGoals, setUserGoals] = useState([]);
	const [allGoals, setAllGoals] = useState([]); // For trainers
	const [goalDeadline, setGoalDeadline] = useState(null);
	const [loading, setLoading] = useState(false);
	const [pbToast, setPbToast] = useState(null); // { movement, value, unit }

	// Default movements and count types (fallback if Firebase collections are empty)
	const DEFAULT_MOVEMENTS = [
		"Deadlift",
		"Back Squat",
		"Front Squat",
		"Bench Press",
		"Overhead Press",
		"Pull-ups",
		"Push-ups",
		"Burpees",
		"Box Jumps",
		"Kettlebell Swings",
		"Thrusters",
		"Clean & Jerk",
		"Snatch",
		"Rowing",
		"Running",
		"Murph",
		"Fran",
		"Grace",
		"Cindy",
		"Helen",
		"Diane",
	];

	const DEFAULT_COUNT_TYPES = [
		{ value: "time", label: "Time", unit: "mm:ss" },
		{ value: "weight", label: "Weight", unit: "kg" },
		{ value: "reps", label: "Reps", unit: "reps" },
		{ value: "rounds", label: "Rounds", unit: "rounds" },
		{ value: "distance", label: "Distance", unit: "m/km" },
		{ value: "calories", label: "Calories", unit: "cals" },
	];

	// Listen for shared workouts (created by trainers)
	useEffect(() => {
		if (!user) return;

		const q = query(collection(db, "workouts"), orderBy("createdAt", "desc"));

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const workouts = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setSharedWorkouts(workouts);
			},
			(error) => {
				console.error("Error listening to workouts:", error);
				setSharedWorkouts([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for user's personal workouts
	useEffect(() => {
		if (!user) return;

		const q = query(
			collection(db, "userWorkouts"),
			where("userId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const workouts = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				// Sort client-side to avoid needing a composite index
				workouts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				setUserWorkouts(workouts);
			},
			(error) => {
				console.error("Error listening to user workouts:", error);
				setUserWorkouts([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for user's records
	useEffect(() => {
		if (!user) return;

		const q = query(
			collection(db, "userRecords"),
			where("userId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				// Sort client-side to avoid needing a composite index
				records.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				setUserRecords(records);
			},
			(error) => {
				console.error("Error listening to user records:", error);
				setUserRecords([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for ALL user records (Trainer only)
	useEffect(() => {
		if (!user || user.role !== "trainer") {
			setAllUserRecords([]);
			return;
		}

		const q = query(
			collection(db, "userRecords"),
			orderBy("createdAt", "desc"),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setAllUserRecords(records);
			},
			(error) => {
				console.error("Error listening to all user records:", error);
				setAllUserRecords([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for movements collection
	useEffect(() => {
		if (!user) return;

		const unsubscribe = onSnapshot(
			doc(db, "gymSettings", "movements"),
			(doc) => {
				if (doc.exists()) {
					setMovements(doc.data().list || DEFAULT_MOVEMENTS);
				} else {
					// Initialize with default movements
					setDoc(doc.ref, {
						list: DEFAULT_MOVEMENTS,
						updatedAt: serverTimestamp(),
					});
					setMovements(DEFAULT_MOVEMENTS);
				}
			},
			(error) => {
				console.error("Error listening to movements:", error);
				setMovements(DEFAULT_MOVEMENTS);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for count types collection
	useEffect(() => {
		if (!user) return;

		const unsubscribe = onSnapshot(
			doc(db, "gymSettings", "countTypes"),
			async (docSnap) => {
				if (docSnap.exists()) {
					const list = docSnap.data().list || DEFAULT_COUNT_TYPES;

					// Migrate any "lbs/kg" units to "kg"
					const needsMigration = list.some((ct) => ct.unit === "lbs/kg");
					if (needsMigration) {
						const migratedList = list.map((ct) =>
							ct.unit === "lbs/kg" ? { ...ct, unit: "kg" } : ct,
						);
						await updateDoc(docSnap.ref, {
							list: migratedList,
							updatedAt: serverTimestamp(),
						});
						// onSnapshot will fire again with the updated data
					} else {
						setCountTypes(list);
					}
				} else {
					// Initialize with default count types
					setDoc(docSnap.ref, {
						list: DEFAULT_COUNT_TYPES,
						updatedAt: serverTimestamp(),
					});
					setCountTypes(DEFAULT_COUNT_TYPES);
				}
			},
			(error) => {
				console.error("Error listening to count types:", error);
				setCountTypes(DEFAULT_COUNT_TYPES);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for user's enrollments
	useEffect(() => {
		if (!user || user.role !== "user") return;

		const q = query(
			collection(db, "enrollments"),
			where("userId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(q, (snapshot) => {
			const enrollments = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setUserEnrollments(enrollments);
		});

		return unsubscribe;
	}, [user]);

	// Listen for notifications
	useEffect(() => {
		if (!user) return;

		const q = query(
			collection(db, "notifications"),
			orderBy("createdAt", "desc"),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const notifs = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setNotifications(notifs);
			},
			(error) => {
				console.error("Error listening to notifications:", error);
				setNotifications([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for workout scores (user's own scores)
	useEffect(() => {
		if (!user) return;

		const q = query(
			collection(db, "workoutScores"),
			where("userId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const scores = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				scores.sort(
					(a, b) => new Date(b.completedAt) - new Date(a.completedAt),
				);
				setWorkoutScores(scores);
			},
			(error) => {
				console.error("Error listening to workout scores:", error);
				setWorkoutScores([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for ALL workout scores (Trainer only)
	useEffect(() => {
		if (!user || user.role !== "trainer") {
			setAllWorkoutScores([]);
			return;
		}

		const q = query(collection(db, "workoutScores"));

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const scores = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				scores.sort(
					(a, b) => new Date(b.completedAt) - new Date(a.completedAt),
				);
				setAllWorkoutScores(scores);
			},
			(error) => {
				console.error("Error listening to all workout scores:", error);
				setAllWorkoutScores([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for trainer planner entries (Trainer only)
	useEffect(() => {
		if (!user || user.role !== "trainer") {
			setPlannerEntries([]);
			return;
		}

		const q = query(
			collection(db, "trainerPlanner"),
			where("trainerId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const entries = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				entries.sort((a, b) => new Date(b.date) - new Date(a.date));
				setPlannerEntries(entries);
			},
			(error) => {
				console.error("Error listening to planner entries:", error);
				setPlannerEntries([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for workshops
	useEffect(() => {
		if (!user) return;

		const q = query(collection(db, "workshops"), orderBy("createdAt", "desc"));

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const items = snapshot.docs.map((d) => ({
					id: d.id,
					...d.data(),
					createdAt:
						d.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setWorkshops(items);
			},
			(error) => {
				console.error("Error listening to workshops:", error);
				setWorkshops([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for user's goals
	useEffect(() => {
		if (!user) return;

		const q = query(collection(db, "goals"), where("userId", "==", user.uid));

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const goals = snapshot.docs.map((d) => ({
					id: d.id,
					...d.data(),
					createdAt:
						d.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				goals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				setUserGoals(goals);
			},
			(error) => {
				console.error("Error listening to user goals:", error);
				setUserGoals([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for ALL goals (Trainer only)
	useEffect(() => {
		if (!user || user.role !== "trainer") {
			setAllGoals([]);
			return;
		}

		const q = query(collection(db, "goals"), orderBy("createdAt", "desc"));

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const goals = snapshot.docs.map((d) => ({
					id: d.id,
					...d.data(),
					createdAt:
						d.data().createdAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setAllGoals(goals);
			},
			(error) => {
				console.error("Error listening to all goals:", error);
				setAllGoals([]);
			},
		);

		return unsubscribe;
	}, [user]);

	// Listen for goal deadline setting
	useEffect(() => {
		if (!user) return;

		const unsubscribe = onSnapshot(
			doc(db, "gymSettings", "goalDeadline"),
			(docSnap) => {
				if (docSnap.exists()) {
					setGoalDeadline(docSnap.data());
				} else {
					setGoalDeadline(null);
				}
			},
			(error) => {
				console.error("Error listening to goal deadline:", error);
				setGoalDeadline(null);
			},
		);

		return unsubscribe;
	}, [user]);

	// Add goal (any user)
	const addGoal = async (goalData) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await addDoc(collection(db, "goals"), {
				...goalData,
				userId: user.uid,
				userName: user.name,
				progressNotes: [],
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error adding goal:", error);
			throw new Error("Failed to save goal");
		} finally {
			setLoading(false);
		}
	};

	// Update goal
	const updateGoal = async (goalId, goalData) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await updateDoc(doc(db, "goals", goalId), {
				...goalData,
				updatedAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error updating goal:", error);
			throw new Error("Failed to update goal");
		} finally {
			setLoading(false);
		}
	};

	// Delete goal
	const deleteGoal = async (goalId) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await deleteDoc(doc(db, "goals", goalId));
		} catch (error) {
			console.error("Error deleting goal:", error);
			throw new Error("Failed to delete goal");
		} finally {
			setLoading(false);
		}
	};

	// Set goal deadline (Trainer only)
	const setGoalDeadlineSetting = async (deadlineData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can set goal deadlines");
		}

		setLoading(true);
		try {
			await setDoc(doc(db, "gymSettings", "goalDeadline"), {
				...deadlineData,
				updatedAt: serverTimestamp(),
				updatedBy: user.uid,
			});
		} catch (error) {
			console.error("Error setting goal deadline:", error);
			throw new Error("Failed to set deadline");
		} finally {
			setLoading(false);
		}
	};

	// Add movement (Trainer only)
	const addMovement = async (movementName) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can add movements");
		}

		if (movements.includes(movementName)) {
			throw new Error("Movement already exists");
		}

		setLoading(true);
		try {
			const newMovements = [...movements, movementName];
			await updateDoc(doc(db, "gymSettings", "movements"), {
				list: newMovements,
				updatedAt: serverTimestamp(),
				updatedBy: user.uid,
			});
		} catch (error) {
			console.error("Error adding movement:", error);
			throw new Error("Failed to add movement");
		} finally {
			setLoading(false);
		}
	};

	// Delete movement (Trainer only)
	const deleteMovement = async (movementName) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete movements");
		}

		// Don't allow deletion if records exist for this movement
		const existingRecords = allUserRecords.filter(
			(r) => r.movement === movementName,
		);
		if (existingRecords.length > 0) {
			throw new Error(
				`Cannot delete movement: ${existingRecords.length} records exist for "${movementName}"`,
			);
		}

		setLoading(true);
		try {
			const newMovements = movements.filter((m) => m !== movementName);
			await updateDoc(doc(db, "gymSettings", "movements"), {
				list: newMovements,
				updatedAt: serverTimestamp(),
				updatedBy: user.uid,
			});
		} catch (error) {
			console.error("Error deleting movement:", error);
			throw new Error("Failed to delete movement");
		} finally {
			setLoading(false);
		}
	};

	// Add count type (Trainer only)
	const addCountType = async (countTypeData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can add count types");
		}

		if (countTypes.find((ct) => ct.value === countTypeData.value)) {
			throw new Error("Count type already exists");
		}

		setLoading(true);
		try {
			const newCountTypes = [...countTypes, countTypeData];
			await updateDoc(doc(db, "gymSettings", "countTypes"), {
				list: newCountTypes,
				updatedAt: serverTimestamp(),
				updatedBy: user.uid,
			});
		} catch (error) {
			console.error("Error adding count type:", error);
			throw new Error("Failed to add count type");
		} finally {
			setLoading(false);
		}
	};

	// Delete count type (Trainer only)
	const deleteCountType = async (countTypeValue) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete count types");
		}

		// Don't allow deletion if records exist for this count type
		const existingRecords = allUserRecords.filter(
			(r) => r.countType === countTypeValue,
		);
		if (existingRecords.length > 0) {
			throw new Error(
				`Cannot delete count type: ${existingRecords.length} records use this type`,
			);
		}

		setLoading(true);
		try {
			const newCountTypes = countTypes.filter(
				(ct) => ct.value !== countTypeValue,
			);
			await updateDoc(doc(db, "gymSettings", "countTypes"), {
				list: newCountTypes,
				updatedAt: serverTimestamp(),
				updatedBy: user.uid,
			});
		} catch (error) {
			console.error("Error deleting count type:", error);
			throw new Error("Failed to delete count type");
		} finally {
			setLoading(false);
		}
	};

	// Add shared workout (Trainer only)
	const addSharedWorkout = async (workoutData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can create shared workouts");
		}

		setLoading(true);
		try {
			const workout = {
				...workoutData,
				createdBy: user.name,
				createdById: user.uid,
				enrolledCount: 0,
				enrolledUsers: [],
				createdAt: serverTimestamp(),
			};

			await addDoc(collection(db, "workouts"), workout);
		} catch (error) {
			console.error("Error adding workout:", error);
			throw new Error("Failed to create workout");
		} finally {
			setLoading(false);
		}
	};

	// Delete shared workout (Trainer only)
	const deleteSharedWorkout = async (workoutId) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete shared workouts");
		}

		setLoading(true);
		try {
			// Also remove all enrollments for this workout
			const enrollmentsQuery = query(
				collection(db, "enrollments"),
				where("workoutId", "==", workoutId),
			);

			const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
			const deletePromises = enrollmentsSnapshot.docs.map((doc) =>
				deleteDoc(doc.ref),
			);
			await Promise.all(deletePromises);

			// Delete the workout
			await deleteDoc(doc(db, "workouts", workoutId));
		} catch (error) {
			console.error("Error deleting workout:", error);
			throw new Error("Failed to delete workout");
		} finally {
			setLoading(false);
		}
	};

	// Add user workout (personal workout log)
	const addUserWorkout = async (workoutData) => {
		if (!user) return;

		setLoading(true);
		try {
			const workout = {
				...workoutData,
				userId: user.uid,
				userName: user.name,
				createdAt: serverTimestamp(),
			};

			await addDoc(collection(db, "userWorkouts"), workout);
		} catch (error) {
			console.error("Error adding user workout:", error);
			throw new Error("Failed to log workout");
		} finally {
			setLoading(false);
		}
	};

	// Delete user workout
	const deleteUserWorkout = async (workoutId) => {
		if (!user) return;

		setLoading(true);
		try {
			await deleteDoc(doc(db, "userWorkouts", workoutId));
		} catch (error) {
			console.error("Error deleting user workout:", error);
			throw new Error("Failed to delete workout");
		} finally {
			setLoading(false);
		}
	};

	// Add user record (personal record or on behalf of another user)
	// Options: { targetUserId, targetUserName } - if provided, saves for that user instead
	const addUserRecord = async (recordData, options = {}) => {
		if (!user) throw new Error("You must be logged in");

		const targetUserId = options.targetUserId || user.uid;
		const targetUserName = options.targetUserName || user.name;
		const isRecordingForOther = targetUserId !== user.uid;

		setLoading(true);
		try {
			const record = {
				...recordData,
				userId: targetUserId,
				userName: targetUserName,
				createdAt: serverTimestamp(),
			};

			// If trainer is recording for another user, stamp who recorded it
			if (isRecordingForOther) {
				record.recordedBy = user.uid;
				record.recordedByName = user.name;
			}

			await addDoc(collection(db, "userRecords"), record);

			// PB Detection (fire-and-forget)
			try {
				// When recording for another user, check PB against their records
				const recordsToCheck = isRecordingForOther
					? allUserRecords.filter(
							(r) =>
								r.userId === targetUserId &&
								r.movement === recordData.movement &&
								r.countType === recordData.countType,
						)
					: userRecords.filter(
							(r) =>
								r.movement === recordData.movement &&
								r.countType === recordData.countType,
						);
				console.log(
					"PB Check:",
					recordData.movement,
					recordData.countType,
					recordData.value,
					"vs",
					recordsToCheck.length,
					"existing records",
					isRecordingForOther ? `(for ${targetUserName})` : "",
				);
				const isPB = detectPB(recordData, recordsToCheck);
				console.log("Is PB:", isPB);
				if (isPB) {
					await createPBMessage(
						targetUserId,
						recordData.movement,
						recordData.value,
						recordData.countUnit,
					);
					console.log("PB message created successfully");
					// Only show toast if recording for yourself
					if (!isRecordingForOther) {
						setPbToast({
							movement: recordData.movement,
							value: recordData.value,
							unit: recordData.countUnit,
						});
					}
				}
			} catch (pbError) {
				console.error("PB detection failed:", pbError);
				// Non-blocking: record is already saved
			}
		} catch (error) {
			console.error("Error adding user record:", error);
			throw new Error("Failed to save movement");
		} finally {
			setLoading(false);
		}
	};

	// Delete user record
	const deleteUserRecord = async (recordId) => {
		if (!user) return;

		setLoading(true);
		try {
			await deleteDoc(doc(db, "userRecords", recordId));
		} catch (error) {
			console.error("Error deleting user record:", error);
			throw new Error("Failed to delete movement");
		} finally {
			setLoading(false);
		}
	};

	// Update user record (edit existing)
	const updateUserRecord = async (recordId, recordData) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await updateDoc(doc(db, "userRecords", recordId), {
				...recordData,
				updatedAt: serverTimestamp(),
			});

			// PB Detection on update (fire-and-forget)
			try {
				const sameMovementRecords = userRecords.filter(
					(r) =>
						r.movement === recordData.movement &&
						r.countType === recordData.countType,
				);
				const isPB = detectPB(recordData, sameMovementRecords, recordId);
				if (isPB) {
					await createPBMessage(
						user.uid,
						recordData.movement,
						recordData.value,
						recordData.countUnit,
					);
					setPbToast({
						movement: recordData.movement,
						value: recordData.value,
						unit: recordData.countUnit,
					});
				}
			} catch (pbError) {
				console.error("PB detection failed:", pbError);
			}
		} catch (error) {
			console.error("Error updating user record:", error);
			throw new Error("Failed to update movement");
		} finally {
			setLoading(false);
		}
	};

	// Enroll in workout
	const enrollInWorkout = async (workoutId) => {
		if (!user || user.role !== "user") return;

		setLoading(true);
		try {
			// Check if already enrolled
			const existingEnrollment = userEnrollments.find(
				(enrollment) => enrollment.workoutId === workoutId,
			);

			if (existingEnrollment) {
				throw new Error("Already enrolled in this workout");
			}

			// Check workout capacity
			const workoutDoc = await getDoc(doc(db, "workouts", workoutId));
			const workoutData = workoutDoc.data();

			if (workoutData.enrolledCount >= workoutData.maxParticipants) {
				throw new Error("Workout is full");
			}

			// Create enrollment
			await addDoc(collection(db, "enrollments"), {
				userId: user.uid,
				userName: user.name,
				workoutId: workoutId,
				enrolledAt: serverTimestamp(),
			});

			// Update workout enrollment count
			await updateDoc(doc(db, "workouts", workoutId), {
				enrolledCount: workoutData.enrolledCount + 1,
				enrolledUsers: arrayUnion(user.uid),
			});
		} catch (error) {
			console.error("Error enrolling in workout:", error);
			throw new Error(error.message || "Failed to enroll in workout");
		} finally {
			setLoading(false);
		}
	};

	// Unenroll from workout
	const unenrollFromWorkout = async (workoutId) => {
		if (!user || user.role !== "user") return;

		setLoading(true);
		try {
			// Find enrollment
			const enrollment = userEnrollments.find((e) => e.workoutId === workoutId);

			if (!enrollment) {
				throw new Error("Not enrolled in this workout");
			}

			// Delete enrollment
			await deleteDoc(doc(db, "enrollments", enrollment.id));

			// Update workout enrollment count
			const workoutDoc = await getDoc(doc(db, "workouts", workoutId));
			const workoutData = workoutDoc.data();

			await updateDoc(doc(db, "workouts", workoutId), {
				enrolledCount: Math.max(0, workoutData.enrolledCount - 1),
				enrolledUsers: arrayRemove(user.uid),
			});
		} catch (error) {
			console.error("Error unenrolling from workout:", error);
			throw new Error(error.message || "Failed to unenroll from workout");
		} finally {
			setLoading(false);
		}
	};

	// Check if user is enrolled in workout
	const isEnrolledInWorkout = (workoutId) => {
		return userEnrollments.some(
			(enrollment) => enrollment.workoutId === workoutId,
		);
	};

	// Add notification (Trainer only)
	const addNotification = async (notificationData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can add notifications");
		}

		setLoading(true);
		try {
			await addDoc(collection(db, "notifications"), {
				...notificationData,
				createdBy: user.name || user.email,
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error adding notification:", error);
			throw error;
		} finally {
			setLoading(false);
		}
	};

	// Delete notification (Trainer only)
	const deleteNotification = async (notificationId) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete notifications");
		}

		setLoading(true);
		try {
			await deleteDoc(doc(db, "notifications", notificationId));
		} catch (error) {
			console.error("Error deleting notification:", error);
			throw error;
		} finally {
			setLoading(false);
		}
	};

	// Add workout score
	const addWorkoutScore = async (scoreData) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await addDoc(collection(db, "workoutScores"), {
				...scoreData,
				userId: user.uid,
				userName: user.name,
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error adding workout score:", error);
			throw new Error("Failed to save score");
		} finally {
			setLoading(false);
		}
	};

	// Delete workout score
	const deleteWorkoutScore = async (scoreId) => {
		if (!user) return;

		setLoading(true);
		try {
			await deleteDoc(doc(db, "workoutScores", scoreId));
		} catch (error) {
			console.error("Error deleting workout score:", error);
			throw new Error("Failed to delete score");
		} finally {
			setLoading(false);
		}
	};

	// Add planner entry (Trainer only)
	const addPlannerEntry = async (entryData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can add planner entries");
		}

		setLoading(true);
		try {
			await addDoc(collection(db, "trainerPlanner"), {
				...entryData,
				trainerId: user.uid,
				trainerName: user.name,
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error adding planner entry:", error);
			throw new Error(error.message || "Failed to add planner entry");
		} finally {
			setLoading(false);
		}
	};

	// Update planner entry (Trainer only)
	const updatePlannerEntry = async (entryId, entryData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can update planner entries");
		}

		setLoading(true);
		try {
			await updateDoc(doc(db, "trainerPlanner", entryId), {
				...entryData,
				updatedAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error updating planner entry:", error);
			throw new Error("Failed to update planner entry");
		} finally {
			setLoading(false);
		}
	};

	// Delete planner entry (Trainer only)
	const deletePlannerEntry = async (entryId) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete planner entries");
		}

		setLoading(true);
		try {
			await deleteDoc(doc(db, "trainerPlanner", entryId));
		} catch (error) {
			console.error("Error deleting planner entry:", error);
			throw new Error("Failed to delete planner entry");
		} finally {
			setLoading(false);
		}
	};

	// Add workshop (Trainer only)
	const addWorkshop = async (workshopData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can add workshops");
		}

		setLoading(true);
		try {
			await addDoc(collection(db, "workshops"), {
				...workshopData,
				published: false,
				createdBy: user.name || user.email,
				createdById: user.uid,
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error adding workshop:", error);
			throw new Error("Failed to add workshop");
		} finally {
			setLoading(false);
		}
	};

	// Update workshop (Trainer only)
	const updateWorkshop = async (workshopId, workshopData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can update workshops");
		}

		setLoading(true);
		try {
			await updateDoc(doc(db, "workshops", workshopId), {
				...workshopData,
				updatedAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error updating workshop:", error);
			throw new Error("Failed to update workshop");
		} finally {
			setLoading(false);
		}
	};

	// Delete workshop (Trainer only)
	const deleteWorkshop = async (workshopId) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can delete workshops");
		}

		setLoading(true);
		try {
			await deleteDoc(doc(db, "workshops", workshopId));
		} catch (error) {
			console.error("Error deleting workshop:", error);
			throw new Error("Failed to delete workshop");
		} finally {
			setLoading(false);
		}
	};

	// Toggle workshop published status (Trainer only)
	const toggleWorkshopPublish = async (workshopId, published) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Only trainers can publish workshops");
		}

		setLoading(true);
		try {
			await updateDoc(doc(db, "workshops", workshopId), {
				published,
				updatedAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Error toggling workshop publish:", error);
			throw new Error("Failed to update workshop");
		} finally {
			setLoading(false);
		}
	};

	const value = {
		// Existing data
		sharedWorkouts: sharedWorkouts.map((workout) => ({
			...workout,
			enrolledCount: workout.enrolledCount || 0,
		})),
		userWorkouts,
		userRecords,
		userEnrollments,
		notifications,
		loading,

		// New trainer data
		allUserRecords,
		movements,
		countTypes,

		// Workout scores
		workoutScores,
		allWorkoutScores,

		// Existing functions
		addSharedWorkout,
		deleteSharedWorkout,
		addUserWorkout,
		deleteUserWorkout,
		addUserRecord,
		deleteUserRecord,
		updateUserRecord,
		enrollInWorkout,
		unenrollFromWorkout,
		isEnrolledInWorkout,

		// New trainer functions
		addMovement,
		deleteMovement,
		addCountType,
		deleteCountType,
		addNotification,
		deleteNotification,
		addWorkoutScore,
		deleteWorkoutScore,

		// Planner functions (Trainer only)
		plannerEntries,
		addPlannerEntry,
		updatePlannerEntry,
		deletePlannerEntry,

		// Workshop data & functions
		workshops,
		addWorkshop,
		updateWorkshop,
		deleteWorkshop,
		toggleWorkshopPublish,

		// Goals data & functions
		userGoals,
		allGoals,
		goalDeadline,
		addGoal,
		updateGoal,
		deleteGoal,
		setGoalDeadlineSetting,

		// PB Toast
		pbToast,
		setPbToast,
	};

	return (
		<WorkoutContext.Provider value={value}>{children}</WorkoutContext.Provider>
	);
}
